/**
 * @fileoverview Category metadata registry
 *
 * Single source of truth for every `KeyDateCategory`:
 *   - Lucide icon (used in list rows, agenda cards, +N more popover,
 *     and the Categories filter swatches)
 *   - Plain-English label (used in list rows, filter, side panel)
 *   - Soft / solid Tailwind colour classes for the leading icon pill
 *
 * All event renderers import from here so the visual vocabulary stays
 * consistent — fix a colour once and every surface picks it up. Keep
 * this file in lock-step with `eventAdapter.colorForEvent` (which
 * decides the *event pill* colour) so the leading icon and the
 * surrounding pill always read as the same category.
 */

import type { LucideIcon } from "lucide-react";
import {
  Flag,
  FileText,
  Users,
  AlertTriangle,
  Sparkles,
  Scale,
  Diamond,
  ListTodo,
  ListTree,
} from "lucide-react";

import type { KeyDateCategory } from "@cvx/keyDates/jobKeyDates/schema";

export interface CategoryMeta {
  /** Practitioner-readable label (used in list rows + filter). */
  label: string;
  /** One-line hint shown in the Categories filter popover. */
  hint: string;
  /** Leading icon. */
  icon: LucideIcon;
  /**
   * Soft pill colour for the leading icon — matches the calendar
   * event pill colour (which `eventAdapter.colorForEvent` returns).
   */
  iconBgClass: string;
  /** Foreground colour for the leading icon on the soft pill. */
  iconFgClass: string;
  /** Small colour swatch used in the Categories filter popover. */
  swatchClass: string;
}

export const CATEGORY_META: Record<KeyDateCategory, CategoryMeta> = {
  appointment: {
    label: "Appointment anchors",
    hint: "Fixed dates associated with appointment e.g. appointment date.",
    icon: Flag,
    iconBgClass: "bg-blue-100 dark:bg-blue-950",
    iconFgClass: "text-blue-700 dark:text-blue-300",
    swatchClass: "bg-blue-500",
  },
  lodgement: {
    label: "Reporting & lodgements",
    hint: "Key lodgement dates associated with job type.",
    icon: FileText,
    iconBgClass: "bg-orange-100 dark:bg-orange-950",
    iconFgClass: "text-orange-700 dark:text-orange-300",
    swatchClass: "bg-orange-500",
  },
  meeting: {
    label: "Meetings",
    hint: "Meetings with creditors, directors, etc.",
    icon: Users,
    iconBgClass: "bg-purple-100 dark:bg-purple-950",
    iconFgClass: "text-purple-700 dark:text-purple-300",
    swatchClass: "bg-purple-500",
  },
  action_deadline: {
    label: "Action deadlines",
    hint: "s588FF / s588M / D&O notification — last date to commence.",
    icon: AlertTriangle,
    iconBgClass: "bg-red-100 dark:bg-red-950",
    iconFgClass: "text-red-700 dark:text-red-300",
    swatchClass: "bg-red-500",
  },
  investigation_window: {
    // Not surfaced on the calendar (lives in the review-windows panel),
    // but kept here so the side panel / fallbacks know what to render
    // if the value ever shows up.
    label: "Investigation windows",
    hint: "Lookback windows for preferences, uncommercials, related-party dealings, PPSA vesting.",
    icon: Scale,
    iconBgClass: "bg-pink-100 dark:bg-pink-950",
    iconFgClass: "text-pink-700 dark:text-pink-300",
    swatchClass: "bg-pink-500",
  },
  milestone: {
    label: "Milestones",
    hint: "Date-anchored milestones that other tasks hang off.",
    icon: Diamond,
    iconBgClass: "bg-violet-100 dark:bg-violet-950",
    iconFgClass: "text-violet-700 dark:text-violet-300",
    swatchClass: "bg-violet-500",
  },
  custom: {
    label: "Custom",
    hint: "Key dates that you have manually added.",
    icon: Sparkles,
    iconBgClass: "bg-neutral-100 dark:bg-neutral-800",
    iconFgClass: "text-neutral-700 dark:text-neutral-300",
    swatchClass: "bg-neutral-400",
  },
};

/**
 * The "type" badge shown on a row's header / card / list icon.
 *
 * Categories (Meetings, Reporting & lodgements, …) are an *event* axis, so
 * they only make sense on event-kind rows. Task-kind rows instead read as
 * **Task**, or **Subtask** when they hang off a parent — regardless of the
 * placeholder category they were stored with. Callers pass `isEvent`
 * (computed via `isEventKind`) to avoid a circular import.
 */
export interface TypeBadgeMeta {
  label: string;
  icon: LucideIcon;
  iconBgClass: string;
  iconFgClass: string;
}

export function typeBadgeMeta(
  isEvent: boolean,
  category: KeyDateCategory,
  hasParent: boolean,
): TypeBadgeMeta {
  if (isEvent) {
    const m = CATEGORY_META[category];
    return {
      label: m.label,
      icon: m.icon,
      iconBgClass: m.iconBgClass,
      iconFgClass: m.iconFgClass,
    };
  }
  return hasParent
    ? {
        label: "Subtask",
        icon: ListTree,
        iconBgClass: "bg-teal-100 dark:bg-teal-950",
        iconFgClass: "text-teal-700 dark:text-teal-300",
      }
    : {
        label: "Task",
        icon: ListTodo,
        iconBgClass: "bg-teal-100 dark:bg-teal-950",
        iconFgClass: "text-teal-700 dark:text-teal-300",
      };
}

/**
 * Stable display order for the Categories filter and the legend. Keeps
 * "Reporting & lodgements" near the top because it's the most common
 * category practitioners filter by.
 */
export const CATEGORY_DISPLAY_ORDER: ReadonlyArray<KeyDateCategory> = [
  "appointment",
  "lodgement",
  "meeting",
  "milestone",
  "custom",
];

/**
 * Every category, in the order the task category dropdowns and filters list
 * them (MOR-707). Unlike `CATEGORY_DISPLAY_ORDER` (the calendar legend) this
 * includes the categories that never render on the calendar surface.
 */
export const TASK_CATEGORY_ORDER: ReadonlyArray<KeyDateCategory> = [
  "appointment",
  "lodgement",
  "meeting",
  "milestone",
  "action_deadline",
  "investigation_window",
  "custom",
];

/** Narrows a raw string (e.g. from a Select's `onValueChange`) to `KeyDateCategory`. */
export function isKeyDateCategory(value: string): value is KeyDateCategory {
  return Object.prototype.hasOwnProperty.call(CATEGORY_META, value);
}
